<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <!-- cv-tool.com Logo -->
      <div class="flex justify-center mb-6">
        <img 
          :src="logoUrl" 
          alt="cv-tool.com" 
          class="h-16 w-auto" 
        />
      </div>
      
      <h2 class="text-center text-3xl font-extrabold text-gray-900 dark:text-white">
        Sign in to your account
      </h2>
      <p class="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
        Or
        <a href="/signup" class="font-medium text-primary-600 hover:text-primary-500">
          create a new account
        </a>
      </p>
    </div>

    <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
      <div class="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
        <form class="space-y-6" @submit.prevent="handleSubmit">
          <!-- Alert for errors -->
          <div v-if="error" class="rounded-md bg-red-50 dark:bg-red-900 p-4">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="ml-3">
                <h3 class="text-sm font-medium text-red-800 dark:text-red-200">
                  {{ error }}
                </h3>
              </div>
            </div>
          </div>

          <div>
            <label for="email" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Email address
            </label>
            <div class="mt-1">
              <input 
                id="email" 
                v-model="email" 
                name="email" 
                type="email" 
                autocomplete="email" 
                required 
                class="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
              >
            </div>
          </div>

          <div>
            <label for="password" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Password
            </label>
            <div class="mt-1">
              <input 
                id="password" 
                v-model="password" 
                name="password" 
                type="password" 
                autocomplete="current-password" 
                required 
                class="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
              >
            </div>
          </div>

          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <input 
                id="remember_me" 
                v-model="rememberMe" 
                name="remember_me" 
                type="checkbox" 
                class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              >
              <label for="remember_me" class="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                Remember me
              </label>
            </div>

            <div class="text-sm">
              <a href="/password/new" class="font-medium text-primary-600 hover:text-primary-500">
                Forgot your password?
              </a>
            </div>
          </div>

          <div>
            <button 
              type="submit" 
              :disabled="isLoading"
              class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              <svg 
                v-if="isLoading" 
                class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" 
                xmlns="http://www.w3.org/2000/svg" 
                fill="none" 
                viewBox="0 0 24 24"
              >
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ isLoading ? 'Signing in...' : 'Sign in' }}
            </button>
          </div>

          <!-- Resend confirmation section -->
          <div v-if="showResendConfirmation" class="mt-4 text-center">
            <p class="text-sm text-gray-600 dark:text-gray-400">
              Your account requires email confirmation. 
              <span class="font-medium text-primary-600 hover:text-primary-500 cursor-pointer" @click="resendConfirmation">
                Resend confirmation email
              </span>
            </p>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuth } from '../composables/useAuth.js'
import logoUrl from '../assets/icons/cv-tool-gold.png'

export default {
  name: 'Login',
  
  setup() {
    const router = useRouter()
    const route = useRoute()
    const email = ref('')
    const password = ref('')
    const rememberMe = ref(false)
    const isLoading = ref(false)
    const error = ref('')
    const showResendConfirmation = ref(false)
    const unconfirmedEmail = ref('')

    const getCSRFToken = () => {
      const meta = document.querySelector('meta[name="csrf-token"]')
      return meta ? meta.getAttribute('content') : null
    }

    const handleSubmit = async () => {
      try {
        isLoading.value = true
        error.value = ''

        const response = await fetch('/api/users/signin', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': getCSRFToken(),
          },
          body: JSON.stringify({
            user: {
              email: email.value,
              password: password.value,
              remember_me: rememberMe.value
            }
          })
        })

        const data = await response.json()

        if (data.success) {
          const { invalidateAuthCache } = useAuth()
          invalidateAuthCache()
          const redirectTo = route.query.redirect || '/dashboard'
          router.push(redirectTo)
        } else {
          error.value = data.message || 'Invalid email or password'
          
          // If user needs email confirmation, show a resend option
          if (data.requires_confirmation) {
            showResendConfirmation.value = true
            unconfirmedEmail.value = data.user_email || email.value
          }
        }
      } catch (err) {
        error.value = 'An error occurred. Please try again.'
        console.error('Login error:', err)
      } finally {
        isLoading.value = false
      }
    }

    const resendConfirmation = async () => {
      if (!unconfirmedEmail.value) return

      try {
        isLoading.value = true
        error.value = ''

        const response = await fetch('/api/users/resend_confirmation', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': getCSRFToken(),
          },
          body: JSON.stringify({
            email: unconfirmedEmail.value
          })
        })

        const data = await response.json()

        if (data.success) {
          alert('Confirmation email resent. Please check your inbox.')
        } else {
          error.value = data.message || 'Failed to resend confirmation email'
        }
      } catch (err) {
        error.value = 'An error occurred. Please try again.'
        console.error('Resend confirmation error:', err)
      } finally {
        isLoading.value = false
      }
    }

    return {
      email,
      password,
      rememberMe,
      isLoading,
      error,
      showResendConfirmation,
      unconfirmedEmail,
      handleSubmit,
      resendConfirmation,
      logoUrl
    }
  }
}
</script>